import { useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";

import Box from "@/components/Box";
import Circle from "@/components/Circle";
import CircleButton from "@/components/CircleButton";
import Tick from "@/images/Tick";
import Accordion, { AccordionContext } from "@/tpo/Accordion";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import { SpanText } from "@/components/Text";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { Badge } from "./Badges";

const PRACTITIONER_TEST_PRODUCTS_QUERY = gql`
  query PractitionerTestProductsForMarkersAssessedQuery($organisation: ID) {
    practitionerTestProducts(organisation: $organisation) {
      id
      paymentIsoCode
      organisation
    }
  }
`;

function UnavailableCrossIcon() {
  return (
    <svg width="13.2" height="13.2" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.0308 1.69707L7.21244 5.51545L11.0308 9.33383L9.33376 11.0309L5.51538 7.21251L1.697 11.0309L-5.14e-05 9.33383L3.81918 5.51459L-5.17372e-05 1.69707L1.697 1.69083e-05L5.51624 3.81754L9.33376 1.69083e-05L11.0308 1.69707Z"
        fill="#1A1A1A"
      />
    </svg>
  );
}

function MarkersAssessedCard({
  title,
  subTitle,
  available = true,
  fontSize = 14,
  lineHeight = "1.2"
}) {
  return (
    <Group
      bg={available ? "#CBE1DA" : "#E5E5E5"}
      borderRadius={4}
      px={14}
      py="9px"
      gap={7}
      alignItems="center"
      maxWidth={224}
    >
      {available ? <Tick color="black" size={13.5} /> : <UnavailableCrossIcon />}
      <Box fontFamily="gilroyMedium" fontSize={fontSize} lineHeight={lineHeight}>
        {title}
      </Box>
      {subTitle ? (
        <Box
          fontFamily="gilroyMedium"
          fontSize={12}
          lineHeight="200%"
          ml={12}
          color="midGrey"
          textTransform="uppercase"
        >
          {subTitle}
        </Box>
      ) : null}
    </Group>
  );
}

function MarkersAccordionSection({ sectionKey, label, count, children }) {
  return (
    <Accordion.Item value={sectionKey}>
      <AccordionContext.Consumer>
        {({ openState, open, close }) => {
          const isOpen = openState.has(sectionKey);

          return (
            <Stack gap={0}>
              <Group
                justifyContent="space-between"
                alignItems="center"
                pb={isOpen ? "0px" : "8px"}
                pt="8px"
              >
                <Group gap={8} alignItems="center">
                  <SpanText
                    fontFamily="gilroyRegular"
                    fontSize={[18, 18, 24]}
                    textTransform="capitalize"
                    lineHeight={["28px", "28px", "36px"]}
                  >
                    {label}:
                  </SpanText>
                  <Circle bg="white" fontFamily="gilroyBold" fontSize={14} size={28} withBorder>
                    {count}
                  </Circle>
                </Group>
                <CircleButton
                  direction={isOpen ? "top" : "bottom"}
                  iconHeight={10}
                  iconWidth={10}
                  onClick={() => (isOpen ? close(sectionKey) : open(sectionKey))}
                />
              </Group>

              <Accordion.Panel p={0} bg="transparent" borderRadius={0}>
                <Stack gap={12} mt={28} pb={28}>
                  {children}
                </Stack>
              </Accordion.Panel>
            </Stack>
          );
        }}
      </AccordionContext.Consumer>
    </Accordion.Item>
  );
}

export default function MarkersAssessed({ markers = [] }) {
  const { organisation } = useOrganisationContext();
  const { data } = useQuery(PRACTITIONER_TEST_PRODUCTS_QUERY, {
    variables: {
      organisation: organisation?.id
    },
    skip: !organisation?.id
  });

  const filteredMarkers = useMemo(() => {
    const practitionerTestProducts = data?.practitionerTestProducts;
    if (!Array.isArray(practitionerTestProducts)) {
      return markers;
    }

    const practitionerTestProductIds = new Set(practitionerTestProducts.map(testProduct => testProduct.id));

    return markers.map(marker => ({
      ...marker,
      testProducts: marker.testProducts.filter(testProduct =>
        practitionerTestProductIds.has(testProduct.id)
      )
    }));
  }, [data?.practitionerTestProducts, markers]);

  const sections = useMemo(() => {
    const grouped = filteredMarkers.reduce((acc, marker) => {
      const testProductFamilies = marker.testProducts.map(testProduct => testProduct.productFamily).filter(pf => pf !== null);
      testProductFamilies.forEach(testProductFamily => {
        if (!acc[testProductFamily.id]) {
          acc[testProductFamily.id] = { id: testProductFamily.id, name: testProductFamily.name, cards: [], testProducts: [] };
        }
        const hasMarkerCard = acc[testProductFamily.id].cards.some(card => card.id === marker.id);
        if (!hasMarkerCard) {
          acc[testProductFamily.id].cards.push({
            id: marker.id,
            title: marker.name,
            available: Boolean(marker.isAvailable),
            testProducts: marker.testProducts,
          });
          acc[testProductFamily.id].testProducts.push(...marker.testProducts.filter(tp => tp.productFamily));
          acc[testProductFamily.id].testProducts = [...new Set(acc[testProductFamily.id].testProducts)];
        }
      });
      return acc;
    }, {});
    return Object.values(grouped);
  }, [filteredMarkers]);

  const [openSections] = useState(() => sections.slice(0, 3).map(section => section.key));
  const [sectionFilters, setSectionFilters] = useState(Object.fromEntries(sections.map(section => [section.id, null])));

  const handleSectionFilterChange = (sectionId, testProductId) => {
    setSectionFilters(prev => ({
      ...prev,
      [sectionId]: testProductId
    }));
  };

  if (!filteredMarkers.length) {
    return null;
  }

  return (
    <Box bg="white" borderRadius={20} px={[20, 20, 40]} pt={[40, 40, 60]} pb={[60, 60, 80]} maxWidth={1024} mx="auto" width="100%">
      <Stack gap={40}>
        <Stack gap={8}>
          <Box
            fontFamily="gilroyBold"
            fontSize={[28, 28, 36]}
            lineHeight={["32px", "32px", "46px"]}
          >
            Markers assessed in report
          </Box>
          <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="24px" mt="-8px">
            The following key markers are all considered in this report
          </Box>
        </Stack>
        <Stack gap={8} pb={28}>
          <Group
            px="16px"
            py="8px"
            borderRadius={100}
            borderColor="dark"
            borderWidth={1}
            borderStyle="solid"
            gap={16}
            mr="auto"
          >
            <Group alignItems="center" gap={8}>
              <Circle bg="#CBE1DA" size={10} />
              <SpanText fontSize={14} lineHeight="24px" fontFamily="gilroyBold">
                Available
              </SpanText>
            </Group>
            <Group alignItems="center" gap={8}>
              <Circle bg="#E5E5E5" size={10} />
              <SpanText fontSize={14} lineHeight="24px" fontFamily="gilroyBold">
                Untested
              </SpanText>
            </Group>
          </Group>
          <Box fontFamily="gilroyMedium" fontSize={[12]} lineHeight="24px">
            Indicates whether marker data is available from current or previous tests
          </Box>
        </Stack>
      </Stack>
      <Accordion variant="default" multiple defaultValue={openSections} size={0}>
        {sections.map(section => (
          <MarkersAccordionSection
            key={section.id}
            sectionKey={section.id}
            label={section.name}
            count={section.cards.length}
          >
            <Group gap={8} flexWrap="wrap">
              <Badge onClick={() => handleSectionFilterChange(section.id, null)} bg={
                sectionFilters[section.id] === null ? "dark" : "light"
              } color={
                sectionFilters[section.id] === null ? "white" : "dark"
              } withBorder>All</Badge>
              {section.testProducts.map(testProduct => (
                <Badge key={testProduct.id} onClick={() => handleSectionFilterChange(section.id, testProduct.id)} bg={
                  sectionFilters[section.id] === testProduct.id ? "dark" : "light"
                } color={
                  sectionFilters[section.id] === testProduct.id ? "white" : "dark"
                } withBorder>{testProduct.name}</Badge>
              ))}
            </Group>
            <Box
              display="grid"
              gridTemplateColumns={["1fr", "1fr 1fr", "repeat(4, minmax(0, 1fr))"]}
              gap={10}
            >
              {section.cards.filter(card => {
                const selectedTestProductId = sectionFilters[section.id];
                if (!selectedTestProductId) {
                  return true;
                }
                return card.testProducts.some(tp => tp.id === selectedTestProductId);
              }).map(card => (
                <MarkersAssessedCard
                  key={card.id}
                  title={card.title}
                  available={card.available}
                  fontSize={[14, 14, 16]}
                  lineHeight={["24px", "24px", "28px"]}
                />
              ))}
            </Box>
          </MarkersAccordionSection>
        ))}
      </Accordion>
    </Box>
  );
}
